/*
 * Copyright 2023 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState } from 'react';
import { ValidateEntityResponse } from '@backstage/catalog-client';
import { useApp } from '@backstage/core-plugin-api';
import { humanizeEntityRef } from '@backstage/plugin-catalog-react';
import WorkIcon from '@mui/icons-material/Work';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MarkdownContent } from '@backstage/core-components';
import { ValidationOutputOk } from '../types';
import { makeStyles } from 'tss-react/mui';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';

const useStyles = makeStyles()(theme => ({
  validationOk: {
    color: theme.palette.success.main,
  },
  validationNotOk: {
    color: theme.palette.error.main,
  },
  errorContainer: {
    color: theme.palette.error.main,
    background: theme.palette.background.paper,
    padding: theme.spacing(2),
  },
}));

type EntityResultProps = {
  isFirstError?: boolean;
  item: ValidationOutputOk;
};

export const EntityResult = ({
  isFirstError = false,
  item,
}: EntityResultProps) => {
  const { classes } = useStyles();
  const app = useApp();
  const [expanded, setExpanded] = useState(isFirstError);

  const Icon =
    app.getSystemIcon(`kind:${item.entity.kind.toLocaleLowerCase('en-US')}`) ??
    WorkIcon;

  const fetchErrorMessages = (response: ValidateEntityResponse) => {
    if (!response.valid) {
      return response.errors.map(err => err.message).join('\n\n');
    }
    return '';
  };

  return (
    <>
      <ListItem key={humanizeEntityRef(item.entity)}>
        <ListItemIcon>
          <Icon
            className={
              item.response.valid
                ? classes.validationOk
                : classes.validationNotOk
            }
          />
        </ListItemIcon>
        <ListItemText
          primary={humanizeEntityRef(item.entity)}
          onClick={() => setExpanded(!expanded)}
        />
        {!item.response.valid && (
          <ListItemSecondaryAction>
            <IconButton
              edge="end"
              onClick={() => setExpanded(!expanded)}
              size="large"
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </ListItemSecondaryAction>
        )}
      </ListItem>
      {!item.response.valid && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Paper className={classes.errorContainer}>
            <MarkdownContent content={fetchErrorMessages(item.response)} />
          </Paper>
        </Collapse>
      )}
    </>
  );
};
